import React, { useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  FormFieldError,
  FormFieldGroup,
  FormFieldGuidance,
  FormFieldInput,
  FormFieldLabel,
  FormFieldSelect,
  FormFieldToggle,
  FormFieldDisclaimer,
  FormikEffect,
  FormikSubmitEffect,
} from '@triumph/shared';
import request from 'helpers/request';
import Label from 'components/_utility/Label';
import { useDispatch, useSelector } from 'react-redux';
import { getAddressFields, getBikeConditions, getFormData, getMakes, getModels } from 'src/redux/app/selectors';
import useLabel from 'src/hooks/useLabel';
import { setFormValues, setMakes, setStep } from 'src/redux/app/actions';
import useDataLayer from 'src/hooks/useDataLayer';
import { STEPPER_HEIGHT } from 'src/config/constants';
import {
  Wrap,
  Title,
  Description,
  FormContainer,
  Rule,
  PXTitle,
  PXDescription,
  ToggleFieldGroup,
  ContinueButton,
} from './YourDetails.styled';

const YourDetails = () => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();
  const wrapRef = useRef();
  const ruleRef = useRef();
  const formData = useSelector(getFormData);
  const makes = useSelector(getMakes);
  const models = useSelector(getModels);
  const getLabel = useLabel();
  const dynamicAddressFields = useSelector(getAddressFields);
  const bikeConditions = useSelector(getBikeConditions);

  useEffect(() => {
    const fetchMakes = async () => {
      const makesRes = await request('/api/v2/makeandmodels/all');
      dispatch(setMakes(makesRes.data));
    };

    fetchMakes();

    pushToDataLayer('purchaseDetails');

    const y = wrapRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - STEPPER_HEIGHT;
    window.scrollTo(0, y);
  }, []);

  const detailsFields = [
    {
      id: 'firstName',
      value: getLabel('firstNameFieldLabel'),
      type: 'input',
      required: true,
      placeholder: getLabel('firstNameFieldPlaceholder'),
      requiredLabel: 'firstNameFieldRequired',
    },
    {
      id: 'lastName',
      value: getLabel('lastNameFieldLabel'),
      type: 'input',
      required: true,
      placeholder: getLabel('lastNameFieldPlaceholder'),
      requiredLabel: 'lastNameFieldRequired',
    },
    {
      id: 'email',
      value: getLabel('emailFieldLabel'),
      type: 'input',
      required: true,
      placeholder: getLabel('emailFieldPlaceholder'),
      requiredLabel: 'emailFieldRequired',
    },
    {
      id: 'phone',
      value: getLabel('phoneFieldLabel'),
      type: 'input',
      required: true,
      guidance: getLabel('phoneFieldText', <></>),
      placeholder: getLabel('phoneFieldPlaceholder'),
      requiredLabel: 'phoneFieldRequired',
    },
  ];
  const partExchangeFields = [
    {
      id: 'make',
      value: getLabel('makeFieldLabel'),
      placeholder: getLabel('pleaseSelectLabel'),
      type: 'select',
      required: true,
      values: makes,
      requiredLabel: 'makeFieldRequired',
      onChange: (event) => dispatch(setFormValues({ ...formData, make: event.target.value })),
    },
    {
      id: 'model',
      value: getLabel('modelFieldLabel'),
      placeholder: getLabel('pleaseSelectLabel'),
      type: 'select',
      required: true,
      values: models,
      requiredLabel: 'modelFieldRequired',
    },
    {
      id: 'registration',
      value: getLabel('registrationFieldLabel'),
      placeholder: getLabel('registrationFieldLabel'),
      type: 'input',
      required: true,
      requiredLabel: 'registrationFieldRequired',
    },
    {
      id: 'mileage',
      value: getLabel('mileageFieldLabel'),
      placeholder: getLabel('mileageFieldLabel'),
      type: 'input',
      required: true,
      requiredLabel: 'mileageFieldRequired',
    },
    {
      id: 'condition',
      value: getLabel('conditionFieldLabel'),
      placeholder: getLabel('pleaseSelectLabel'),
      type: 'select',
      required: true,
      values: bikeConditions,
      requiredLabel: 'conditionFieldRequired',
    },
  ];

  const schemaWithAddress = dynamicAddressFields.reduce(
    (prevSchema, addressField) => ({
      ...prevSchema,
      [addressField.id]: Yup.string().required(addressField.requiredLabel),
    }),
    {
      firstName: Yup.string().required('firstNameFieldRequired'),
      lastName: Yup.string().required('lastNameFieldRequired'),
      email: Yup.string()
        .email('emailFieldValidation')
        .required('emailFieldRequired'),
      phone: Yup.string().required('phoneFieldRequired'),
    }
  );
  const schemaPartExchange = partExchangeFields.reduce(
    (prevSchema, partExchangeField) => ({
      ...prevSchema,
      [partExchangeField.id]: Yup.string().when('partExchange', {
        is: 'yes',
        then: Yup.string().required(partExchangeField.requiredLabel),
      }),
    }),
    {}
  );
  const YourDetailsSchema = Yup.object().shape({ ...schemaWithAddress, ...schemaPartExchange });

  const initialValues = dynamicAddressFields.reduce(
    (prevValues, addressField) => ({
      ...prevValues,
      [addressField.id]:
        addressField.type === 'select'
          ? formData[addressField.id] || addressField.values.find((option) => option.selected)?.id || ''
          : formData[addressField.id] || addressField.defaultValue || '',
    }),
    {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      partExchange: formData.partExchange,
      make: formData.make,
      model: formData.model,
      registration: formData.registration,
      mileage: formData.mileage,
      condition: formData.condition,
    }
  );

  const handleSubmit = (values) => {
    dispatch(setFormValues(values));
    dispatch(setStep(2));
  };

  const renderLabel = ({ value, required, text }) => (
    <FormFieldLabel hasDescription={Boolean(text)}>
      {value}
      {required ? (
        <span className="required">*</span>
      ) : (
        <span className="optional">
          <Label name="optionalLabel"></Label>
        </span>
      )}
      {text && <div className="description">{text}</div>}
    </FormFieldLabel>
  );

  const renderFormFieldGroup = ({ field, values, errors, handleChange }) => {
    const getOptionDescription = (selectValue, options) =>
      selectValue && options.find((option) => option.id === selectValue)?.description;

    const onChange = (event) => {
      if (typeof field.onChange === 'function') {
        field.onChange(event);
      }

      handleChange(event);
    };

    return (
      <FormFieldGroup key={field.id}>
        {renderLabel(field)}
        {(field.type === 'input' || !field.type) && (
          <FormFieldInput
            name={field.id}
            placeholder={field.placeholder}
            value={values[field.id]}
            onChange={onChange}
            disabled={field.disabled}
            error={field.required && errors[field.id]}
          />
        )}
        {field.type === 'select' && (
          <>
            <FormFieldSelect
              name={field.id}
              disabled={field.disabled}
              onChange={onChange}
              value={values[field.id] || ''}
            >
              <option value="" disabled>
                {field.placeholder || field.value}
              </option>
              {field.values.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </FormFieldSelect>
            {getOptionDescription(values[field.id], field.values) && (
              <FormFieldDisclaimer>{getOptionDescription(values[field.id], field.values)}</FormFieldDisclaimer>
            )}
          </>
        )}
        {field.guidance && <FormFieldGuidance>{field.guidance}</FormFieldGuidance>}
        {field.required && errors[field.id] && (
          <FormFieldError>
            <Label name={errors[field.id]}></Label>
          </FormFieldError>
        )}
      </FormFieldGroup>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={YourDetailsSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {({ values, errors, handleChange, setFieldValue, validateField }) => (
        <Wrap ref={wrapRef}>
          <Form>
            <FormikEffect
              values={values}
              onChange={(currentFormikState, nextFormikState) => {
                const keys = Object.keys(currentFormikState);
                keys.forEach(key => {
                  if(currentFormikState[key] !== nextFormikState[key] && errors[key]){
                    validateField(key);
                  }
                });
              }}
            />
            <FormikSubmitEffect
              onSubmitError={() => {
                let jumpToEl = wrapRef.current;

                const yourDetailsFieldNames = [...detailsFields, ...dynamicAddressFields].map((field) => field.id);
                const isYourDetailsError = yourDetailsFieldNames.some((fieldName) => Boolean(errors[fieldName]));

                if (!isYourDetailsError) {
                  jumpToEl = ruleRef.current;
                }

                const y =
                  jumpToEl.getBoundingClientRect().top - document.body.getBoundingClientRect().top - STEPPER_HEIGHT;
                window.scrollTo(0, y);
              }}
            ></FormikSubmitEffect>
            <Title>
              <Label name="customerDetailsTitle"></Label>
            </Title>
            <Description>
              <Label name="customerDetailsDescription"></Label>
            </Description>
            <FormContainer>
              {[...detailsFields, ...dynamicAddressFields].map((addressField) =>
                renderFormFieldGroup({ field: addressField, values, errors, handleChange })
              )}
            </FormContainer>
            <Rule ref={ruleRef} />
            <FormContainer>
              <PXTitle>
                <Label name="tradeInTitle"></Label>
              </PXTitle>
              <PXDescription>
                <Label name="tradeInDescription"></Label>
              </PXDescription>
              <ToggleFieldGroup>
                <FormFieldToggle
                  name="partExchange"
                  value={values.partExchange}
                  options={[
                    {
                      id: 'yes',
                      value: getLabel('tradeInYesButtonLabel'),
                    },
                    {
                      id: 'no',
                      value: getLabel('tradeInNoButtonLabel'),
                    },
                  ]}
                  onChange={(event) => {
                    if (event.target.value === 'no') {
                      setFieldValue('make', '');
                      setFieldValue('model', '');
                      setFieldValue('registration', '');
                      setFieldValue('mileage', '');
                      setFieldValue('condition', '');
                    }

                    handleChange(event);
                  }}
                ></FormFieldToggle>
              </ToggleFieldGroup>
              {values.partExchange === 'yes' &&
                partExchangeFields.map((partExchangeField) =>
                  renderFormFieldGroup({ field: partExchangeField, values, errors, handleChange })
                )}
            </FormContainer>
            <ContinueButton type="submit" primary fluid>
              <Label name="detailsContinueButtonLabel"></Label>
            </ContinueButton>
          </Form>
        </Wrap>
      )}
    </Formik>
  );
};

export default YourDetails;
